import { type Ref, computed, ref, watch } from 'vue'
import {
  type ChatMentionCandidate,
  extractMentionQuery,
  filterMentionCandidates,
} from '../utils/mentions'

interface UseChatMentionsParams {
  text: Ref<string>
  candidates: Ref<ChatMentionCandidate[]>
  excludeKey?: Ref<string | null | undefined>
  getCursor: () => number
  setTextAndCursor: (value: string, cursor: number) => void
}

/** @-упоминания участников: открытие пикера, навигация, вставка */
export function useChatMentions(params: UseChatMentionsParams) {
  const isOpen = ref(false)
  const query = ref('')
  const startIndex = ref(0)
  const highlightedIndex = ref(0)

  const filtered = computed(() =>
    filterMentionCandidates(
      params.candidates.value,
      query.value,
      params.excludeKey?.value,
    ),
  )

  watch(filtered, list => {
    if (highlightedIndex.value >= list.length) {
      highlightedIndex.value = Math.max(0, list.length - 1)
    }
  })

  const syncFromCursor = (textOverride?: string) => {
    const text = textOverride ?? params.text.value
    const cursor = Math.min(params.getCursor(), text.length)
    const mention = extractMentionQuery(text, cursor)
    if (!mention) {
      isOpen.value = false
      query.value = ''
      return
    }

    startIndex.value = mention.start
    query.value = mention.query
    isOpen.value = true
    highlightedIndex.value = 0
  }

  const close = () => {
    isOpen.value = false
    query.value = ''
  }

  const select = (candidate: ChatMentionCandidate) => {
    const text = params.text.value
    const cursor = Math.min(params.getCursor(), text.length)
    const before = text.slice(0, startIndex.value)
    const after = text.slice(cursor)
    const insertion = `@${candidate.mentionText} `
    const next = `${before}${insertion}${after}`
    const nextCursor = before.length + insertion.length

    params.setTextAndCursor(next, nextCursor)
    close()
  }

  const selectHighlighted = () => {
    const target = filtered.value[highlightedIndex.value] ?? filtered.value[0]
    if (!target) return false
    select(target)
    return true
  }

  /** true — событие обработано пикером (не слать сообщение) */
  const onKeydown = (event: KeyboardEvent): boolean => {
    if (!isOpen.value) return false

    if (event.key === 'Escape') {
      event.preventDefault()
      close()
      return true
    }

    if (filtered.value.length === 0) return false

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      highlightedIndex.value = (highlightedIndex.value + 1) % filtered.value.length
      return true
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      highlightedIndex.value
        = (highlightedIndex.value - 1 + filtered.value.length) % filtered.value.length
      return true
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      selectHighlighted()
      return true
    }

    if (event.key === 'Tab') {
      event.preventDefault()
      selectHighlighted()
      return true
    }

    return false
  }

  return {
    isOpen,
    query,
    filtered,
    highlightedIndex,
    syncFromCursor,
    close,
    select,
    onKeydown,
  }
}
